import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  Input,
  Text,
  LoadingSpinner,
  SortableTableHeader,
} from '../../components/ui'
import { HorizontalTableScroll } from './HorizontalTableScroll'
import { SearchableCheckboxDropdown } from './SearchableCheckboxDropdown'
import { financeiroService } from '../../services/financeiro'
import { formatMoneyBr } from '../../utils/financeiroComissoesImport'
import type {
  FinanceiroComissoesIcatuLinha,
  FinanceiroComissoesIcatuPasta,
} from '../../types/financeiro'
import {
  CARD_CLASS,
  ERROR_BANNER_CLASS,
  FIELD_LABEL_CLASS,
  FILTER_CARD_CLASS,
  PAGE_SIZE,
  PAGINATION_BTN_CLASS,
  formatDatePtBr,
  paginationPageClass,
} from './financeiroPelUi'

type SortKey = keyof FinanceiroComissoesIcatuLinha

const COLUMN_DEFS: Array<{ key: SortKey; label: string }> = [
  { key: 'nome_corretor', label: 'Nome do Corretor' },
  { key: 'cpf_cnpj', label: 'CPF/CNPJ' },
  { key: 'tipo_comissao', label: 'Tipo de Comissão' },
  { key: 'valor_comissao', label: 'Valor da Comissão' },
  { key: 'data_pagamento_efetivo', label: 'Data Pagamento Efetivo' },
  { key: 'status_recibo', label: 'Status Recibo' },
]

const MONEY_KEYS = new Set<SortKey>(['valor_comissao'])

const DATE_KEYS = new Set<SortKey>(['data_pagamento_efetivo'])

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message
  if (
    err &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as { message: unknown }).message === 'string'
  ) {
    return (err as { message: string }).message
  }
  return fallback
}

const ComissoesConsultaTab: React.FC = () => {
  const [pastas, setPastas] = useState<FinanceiroComissoesIcatuPasta[]>([])
  const [rows, setRows] = useState<FinanceiroComissoesIcatuLinha[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [folderNames, setFolderNames] = useState<string[]>([])
  const [dataPagamentoEfetivo, setDataPagamentoEfetivo] = useState('')
  const [tipoComissao, setTipoComissao] = useState('')
  const [statusRecibo, setStatusRecibo] = useState('')
  const [search, setSearch] = useState('')
  const [appliedFolderNames, setAppliedFolderNames] = useState<string[]>([])
  const [appliedDataPagamentoEfetivo, setAppliedDataPagamentoEfetivo] = useState('')
  const [appliedTipoComissao, setAppliedTipoComissao] = useState('')
  const [appliedStatusRecibo, setAppliedStatusRecibo] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')

  const [sortBy, setSortBy] = useState<SortKey>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const requestIdRef = useRef(0)

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const startIndex = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE
  const endIndex = Math.min(startIndex + PAGE_SIZE, total)

  const loadPastas = useCallback(async () => {
    try {
      const list = await financeiroService.listPastasProcessadas()
      setPastas(list)
    } catch (err) {
      console.error(err)
    }
  }, [])

  const scopedFolderNames =
    appliedFolderNames.length > 0 && appliedFolderNames.length < pastas.length
      ? appliedFolderNames
      : null
  const folderKey = scopedFolderNames?.slice().sort().join('\0') ?? ''
  const consultaFilters = {
    folderNames: scopedFolderNames,
    dataPagamentoEfetivo: appliedDataPagamentoEfetivo || null,
    tipoComissao: appliedTipoComissao || null,
    statusRecibo: appliedStatusRecibo || null,
    search: appliedSearch || null,
  }

  const loadRows = useCallback(async () => {
    const requestId = ++requestIdRef.current
    setLoading(true)
    setError(null)
    try {
      const result = await financeiroService.listComissoes({
        ...consultaFilters,
        sortBy,
        sortOrder,
        page: currentPage,
        pageSize: PAGE_SIZE,
      })
      if (requestId !== requestIdRef.current) return
      setRows(result.rows)
      setTotal(result.total)
    } catch (err) {
      if (requestId !== requestIdRef.current) return
      console.error(err)
      setError(errorMessage(err, 'Erro ao carregar comissões.'))
      setRows([])
      setTotal(0)
    } finally {
      if (requestId === requestIdRef.current) setLoading(false)
    }
  }, [
    folderKey,
    appliedDataPagamentoEfetivo,
    appliedTipoComissao,
    appliedStatusRecibo,
    appliedSearch,
    sortBy,
    sortOrder,
    currentPage,
  ])

  useEffect(() => {
    void loadPastas()
  }, [loadPastas])

  useEffect(() => {
    if (!searched) return
    void loadRows()
  }, [loadRows, searched])

  const applyFilters = () => {
    setAppliedFolderNames(folderNames)
    setAppliedDataPagamentoEfetivo(dataPagamentoEfetivo)
    setAppliedTipoComissao(tipoComissao.trim())
    setAppliedStatusRecibo(statusRecibo.trim())
    setAppliedSearch(search.trim())
    setCurrentPage(1)
    setSearched(true)
  }

  const clearFilters = () => {
    requestIdRef.current += 1
    setFolderNames([])
    setDataPagamentoEfetivo('')
    setTipoComissao('')
    setStatusRecibo('')
    setSearch('')
    setAppliedFolderNames([])
    setAppliedDataPagamentoEfetivo('')
    setAppliedTipoComissao('')
    setAppliedStatusRecibo('')
    setAppliedSearch('')
    setCurrentPage(1)
    setSearched(false)
    setRows([])
    setTotal(0)
    setError(null)
    setLoading(false)
  }

  const toggleSort = (key: string) => {
    const sortKey = key as SortKey
    if (sortBy === sortKey) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(sortKey)
      setSortOrder('asc')
    }
    setCurrentPage(1)
  }

  return (
    <div className="space-y-6">
      {error && <div className={ERROR_BANNER_CLASS}>{error}</div>}

      <form
        className={FILTER_CARD_CLASS}
        onSubmit={(event) => {
          event.preventDefault()
          applyFilters()
        }}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Pasta</label>
            <SearchableCheckboxDropdown
              options={pastas.map((p) => ({
                value: p.folder_name,
                label: `${p.folder_name} (${p.row_count})`,
              }))}
              selected={folderNames}
              onChange={setFolderNames}
              emptyLabel="Todas as pastas"
              searchPlaceholder="Buscar pasta..."
              multipleLabel={(count) => `${count} pastas`}
            />
          </div>
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Data pagamento efetivo</label>
            <Input
              type="date"
              className="w-full min-w-0"
              value={dataPagamentoEfetivo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDataPagamentoEfetivo(e.target.value)
              }
            />
          </div>
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Status recibo</label>
            <Input
              className="w-full"
              value={statusRecibo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setStatusRecibo(e.target.value)
              }
              placeholder="Status do recibo"
            />
          </div>
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Tipo de comissão</label>
            <Input
              className="w-full"
              value={tipoComissao}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTipoComissao(e.target.value)
              }
              placeholder="Tipo de comissão"
            />
          </div>
          <div className="min-w-0 sm:col-span-2 lg:col-span-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="min-w-0 flex-1">
                <label className={FIELD_LABEL_CLASS}>Busca</label>
                <Input
                  className="w-full"
                  value={search}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                  placeholder="Corretor, código, CPF/CNPJ (com ou sem máscara), apólice ou fatura..."
                />
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <Button type="submit" disabled={loading}>
                  Buscar
                </Button>
                <Button type="button" variant="outline" onClick={clearFilters} disabled={loading}>
                  Limpar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </form>

      <div className={CARD_CLASS}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <HorizontalTableScroll showNavButtons={false}>
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                  <tr>
                    {COLUMN_DEFS.map((col) => (
                      <SortableTableHeader
                        key={col.key}
                        sortKey={col.key}
                        currentSort={sortBy}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        compact
                      >
                        {col.label}
                      </SortableTableHeader>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={COLUMN_DEFS.length}
                        className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400"
                      >
                        {searched
                          ? 'Nenhum registro encontrado'
                          : 'Clique em Buscar para carregar os resultados'}
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr
                        key={row.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        {COLUMN_DEFS.map((col) => {
                          const raw = row[col.key]
                          const display = MONEY_KEYS.has(col.key)
                            ? formatMoneyBr(raw as number | null)
                            : DATE_KEYS.has(col.key)
                              ? formatDatePtBr(raw as string | null)
                              : raw === null || raw === undefined || raw === ''
                                ? '—'
                                : String(raw)
                          return (
                            <td
                              key={col.key}
                              className={`px-3 py-2 text-sm whitespace-nowrap text-gray-900 dark:text-gray-100 ${
                                MONEY_KEYS.has(col.key) ? 'tabular-nums text-right' : ''
                              }`}
                            >
                              {display}
                            </td>
                          )
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </HorizontalTableScroll>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Text size="sm" className="text-gray-700 dark:text-gray-300">
                  {total === 0
                    ? 'Nenhum registro'
                    : `Mostrando ${startIndex + 1} a ${endIndex} de ${total.toLocaleString('pt-BR')} registro${total !== 1 ? 's' : ''}`}
                </Text>

                {totalPages > 1 && (
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={PAGINATION_BTN_CLASS}
                    >
                      Anterior
                    </button>
                    <div className="flex space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNumber: number
                        if (totalPages <= 5) {
                          pageNumber = i + 1
                        } else if (currentPage <= 3) {
                          pageNumber = i + 1
                        } else if (currentPage >= totalPages - 2) {
                          pageNumber = totalPages - 4 + i
                        } else {
                          pageNumber = currentPage - 2 + i
                        }
                        return (
                          <button
                            key={pageNumber}
                            type="button"
                            onClick={() => setCurrentPage(pageNumber)}
                            className={paginationPageClass(currentPage === pageNumber)}
                          >
                            {pageNumber}
                          </button>
                        )
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className={PAGINATION_BTN_CLASS}
                    >
                      Próxima
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ComissoesConsultaTab
